// src/pages/Admin/Dashboard/index.jsx
// דשבורד מנהל ארגון

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import customFetch from '../../../api/customFetch';
import { useAuthState } from '../../../context/AuthContext';

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [exams, setExams] = useState([]);
  const [sectorStats, setSectorStats] = useState([]);
  const [sectorTotals, setSectorTotals] = useState(null);

  const nav = useNavigate();
  const { user: currentUser } = useAuthState();

  const role = currentUser?.role;
  const canSeeOrgWide = role === 'org_manager' || role === 'admin';

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const results = await Promise.allSettled([
        customFetch.get('/users/sector-stats'),
        customFetch.get('/courses/admin'),
        customFetch.get('/users/all'),
        customFetch.get('/exams/'),
      ]);

      console.log(results[0].value.data);
      
      if (results[0].status === 'fulfilled') {
        const d = results[0].value.data || {};
        setSectorStats(d.sectorStats || []);
        setSectorTotals(d.totals || null);
      }
      if (results[1].status === 'fulfilled') {
        const d = results[1].value.data;
        setCourses(d?.courses || d || []);
      }
      if (results[2].status === 'fulfilled') {
        setUsers(results[2].value.data?.users || []);
      }
      if (results[3].status === 'fulfilled') {
        setExams(results[3].value.data?.exams || []);
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
      if (err?.response?.status === 401) {
        nav('/admin/login');
        return;
      }
      setError('שגיאה בטעינת הדשבורד');
    } finally {
      setLoading(false);
    }
  };

  const orgStats = useMemo(
    () => ({
      totalCourses: courses.length,
      publishedCourses: courses.filter((c) => c?.isPublished).length,
      totalUsers: users.length,
      pendingUsers: users.filter((u) => !u?.isApproved && u?.role !== 'admin')
        .length,
      approvedUsers: users.filter((u) => u?.isApproved && u?.isActive).length,
      totalExams: exams.length,
      activeExams: exams.filter((e) => e?.isActive).length,
    }),
    [courses, users, exams]
  );

  if (loading) {
    return (
      <div className='admin-page'>
        <div className='loading-container'>
          <div className='loading-spinner'></div>
          <span>טוען דשבורד...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='admin-page'>
        <div className='admin-alert error'>{error}</div>
        <button className='admin-btn primary' onClick={loadDashboardData}>
          נסה שוב
        </button>
      </div>
    );
  }

  return (
    <div className='admin-page'>
      <div className='page-header'>
        <h1 className='page-title'>
          <span className='page-title-icon'>🏢</span>
          דשבורד מנהל ארגון
        </h1>
        <button className='admin-btn secondary' onClick={loadDashboardData}>
          🔄 רענן
        </button>
      </div>

      {/* סטטיסטיקות ארגון */}
      {canSeeOrgWide && (
        <div className='stats-grid'>
          <div className='stat-card' onClick={() => nav('/admin/users')}>
            <div className='stat-icon blue'>👥</div>
            <div className='stat-content'>
              <div className='stat-value'>{orgStats.totalUsers}</div>
              <div className='stat-label'>משתמשים</div>
              {orgStats.pendingUsers > 0 && (
                <div className='stat-change warning'>
                  ⏳ {orgStats.pendingUsers} ממתינים
                </div>
              )}
            </div>
          </div>

          <div className='stat-card' onClick={() => nav('/admin/courses')}>
            <div className='stat-icon green'>📚</div>
            <div className='stat-content'>
              <div className='stat-value'>{orgStats.totalCourses}</div>
              <div className='stat-label'>לומדות</div>
            </div>
          </div>

          <div className='stat-card' onClick={() => nav('/admin/exams')}>
            <div className='stat-icon purple'>📝</div>
            <div className='stat-content'>
              <div className='stat-value'>{orgStats.totalExams}</div>
              <div className='stat-label'>מבחנים</div>
            </div>
          </div>
        </div>
      )}
      
      {/* סטטיסטיקות סקטור */}
      <div className='admin-card'>
        <div className='card-header'>
          <h3 className='card-title'>📌 סטטיסטיקות לפי סקטור</h3>
        </div>
        {sectorStats.length === 0 ? (
          <p style={{ color: 'var(--admin-text-muted)', padding: 16 }}>
            אין נתונים
          </p>
        ) : (
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              color: 'var(--admin-text)',
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: 12,
                    textAlign: 'right',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  סקטור
                </th>
                <th
                  style={{
                    padding: 12,
                    textAlign: 'right',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  עובדים
                </th>
                <th
                  style={{
                    padding: 12,
                    textAlign: 'right',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  התקדמות
                </th>
                <th
                  style={{
                    padding: 12,
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                  }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {sectorStats.map((s) => (
                <tr key={s.sector}>
                  <td
                    style={{
                      padding: 12,
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    {s.sector}
                  </td>
                  <td
                    style={{
                      padding: 12,
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    {s.totalEmployees || 0}
                  </td>
                  <td
                    style={{
                      padding: 12,
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    {s.averageProgress || 0}%
                  </td>
                  <td
                    style={{
                      padding: 12,
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <button
                      className='admin-btn secondary sm'
                      onClick={() => nav(`/admin/users?sector=${s.sector}`)}
                    >
                      👥 הצג עובדים
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
